import {
  Center,
  Box,
  Flex,
  Text,
  Link,
  Button,
  SimpleGrid,
  Show,
  BoxProps,
} from "@chakra-ui/react";
import BaseImage from "@components/BaseImage";
import { NAV_ITEMS } from "@components/HeaderNav/NavData";
import UTMLink from "@components/UTMLink";
import { LP_FOOTER, LP_RESTAKE, LP_SOCIAL } from "@constants/analytics";
import { COMMUNITY_DETAILS, getCommunityIcon } from "@constants/common";
import { SideBarDataType } from "@constants/types";
import useDappConfig from "@hooks/useDappConfig";
import { Fragment } from "react";
import { trackEvent } from "utils";

const UtmLinkClick = ({
  navItem,
  event,
}: {
  navItem: SideBarDataType;
  event?: string;
}) => {
  return (
    <UTMLink
      href={navItem.link ?? "#"}
      isExternal={navItem.isExternal}
      key={navItem.key}
      onClick={() => {
        trackEvent(event || navItem.eventName || LP_FOOTER, {
          cta: navItem.key,
          cta_text: navItem.title,
        });
      }}
    >
      <Flex
        _hover={{
          textDecoration: "none",
          opacity: 1,
        }}
        // color="textPrimary"
        gap={2}
      >
        {navItem.title}
        {navItem.isNew && (
          <BaseImage
            src={"/assets/sidebar/newTag.svg"}
            alt={""}
            height={24}
            width={23}
            loading="eager"
          />
        )}
      </Flex>
    </UTMLink>
  );
};

const Options = ({ options }: { options: SideBarDataType[] }) => {
  return (
    <Flex
      flexDir="column"
      gap="12px"
      opacity="0.7"
      color="#FFF"
      fontSize={{ basse: "12px", md: "18px" }}
      fontWeight="500"
    >
      {options?.map((data: SideBarDataType) => {
        return (
          <Fragment key={data.key}>
            <UtmLinkClick navItem={data} />
          </Fragment>
        );
      })}
    </Flex>
  );
};

const FooterSectionTitle = ({ title }: { title: string }) => {
  return (
    <Text
      color="#FFF"
      fontWeight="700"
      fontSize={{ base: "20px", md: "22px" }}
      lineHeight={{ base: "24px", md: "26px" }}
    >
      {title}
    </Text>
  );
};

const getNavItem = (key: string) => {
  return NAV_ITEMS.find((data) => data.key === key);
};

const Footer = ({ ...props }: BoxProps) => {
  const { restakeEnabledChains } = useDappConfig();

  const networks: SideBarDataType[] = restakeEnabledChains.map((chain) => {
    return {
      title: chain.name,
      key: chain.id.toString(),
      link: `/defi?chain=${chain.id}`,
      isExternal: true,
    };
  });

  const avsItem = getNavItem("avs");
  const aboutItem = getNavItem("about");

  return (
    <Center
      w="100%"
      bg="#1B443D"
      pb={{ base: "40px", md: "24px" }}
      pt={{ base: "40px", md: "80px" }}
      {...props}
    >
      <Box maxW="1120" p="0 16px" w="100%">
        <Flex flexDir="column" gap={{ base: "40px", md: 0 }}>
          <Flex
            alignItems={{ base: "flex-start", md: "center" }}
            w="100%"
            flexDir={{ base: "column", md: "row" }}
            gap={{ base: "40px", md: "128px" }}
          >
            <Flex
              alignItems="flex-start"
              justifyContent="space-between"
              color="white"
              gap="128px"
            >
              <Flex flexDir="column" gap="24px" alignItems="flex-start">
                <BaseImage
                  src="/assets/kelp_white.svg"
                  alt="kelp"
                  height={48}
                  width={128}
                />

                <Flex gap="8px">
                  {COMMUNITY_DETAILS.map((data) => (
                    <Link
                      cursor="pointer"
                      href={data.link}
                      target="_blank"
                      rel="nofollow"
                      key={data.name}
                      onClick={() => {
                        trackEvent(LP_SOCIAL, {
                          redirect_link: data.link,
                          cta_location: "Footer",
                          cta: data.name,
                        });
                      }}
                    >
                      <Center
                        key={data.key}
                        borderRadius="50%"
                        bg="backgroundDivider"
                        w="40px"
                        h="40px"
                      >
                        {data.key && getCommunityIcon(data.key) ? (
                          <BaseImage
                            width={24}
                            height={24}
                            src={getCommunityIcon(data.key) || ""}
                            alt={data.key}
                          />
                        ) : null}
                      </Center>
                    </Link>
                  ))}
                </Flex>

                <UTMLink
                  href="/restake"
                  _hover={{
                    borderBottom: "none",
                  }}
                  onClick={() => {
                    trackEvent(LP_RESTAKE, {
                      triggerer: "footer",
                    });
                  }}
                >
                  <Button
                    variant="unstyled"
                    p="16px 64px"
                    borderRadius="16px"
                    border="1px solid #F7F9F9"
                    h="56px"
                    fontWeight="600"
                    lineHeight="24px"
                    fontSize="14px"
                    color="#F7F9F9"
                  >
                    Restake now
                  </Button>
                </UTMLink>
              </Flex>
            </Flex>
            <Show above="769px">
              <SimpleGrid w="100%" spacing="40px" columns={3}>
                <Flex flexDir="column" gap="16px" w="231px" maxW="100%">
                  <FooterSectionTitle title="Products" />
                  <Options options={getNavItem("products")?.data || []} />
                </Flex>
                <Flex flexDir="column" gap="16px" w="231px" maxW="100%">
                  <FooterSectionTitle title="Networks" />
                  <Options options={networks || []} />
                </Flex>
                <Flex flexDir="column" gap="40px" w="231px" maxW="100%">
                  {avsItem && (
                    <Text
                      color="#FFF"
                      fontWeight="700"
                      fontSize="22px"
                      lineHeight="32px"
                    >
                      <UtmLinkClick navItem={avsItem} />
                    </Text>
                  )}
                  {aboutItem && (
                    <Text
                      color="#FFF"
                      fontWeight="700"
                      fontSize="22px"
                      lineHeight="32px"
                    >
                      <UtmLinkClick navItem={aboutItem} />
                    </Text>
                  )}
                  <Flex flexDir="column" gap="16px">
                    <FooterSectionTitle title="Insights" />
                    <Options options={getNavItem("insights")?.data || []} />
                  </Flex>
                </Flex>
              </SimpleGrid>
            </Show>
            <Show below="md">
              <SimpleGrid w="100%" spacing="40px" columns={2}>
                <Flex flexDir="column" gap="16px">
                  <FooterSectionTitle title="Products" />
                  <Options options={getNavItem("products")?.data || []} />
                </Flex>
                <Flex flexDir="column" gap="16px">
                  <FooterSectionTitle title="Networks" />
                  <Options options={networks || []} />
                </Flex>
                {avsItem && (
                  <Text
                    color="#FFF"
                    fontWeight="700"
                    fontSize="22px"
                    lineHeight="32px"
                  >
                    <UtmLinkClick navItem={avsItem} />
                  </Text>
                )}

                {aboutItem && (
                  <Text
                    color="#FFF"
                    fontWeight="700"
                    fontSize="22px"
                    lineHeight="32px"
                  >
                    <UtmLinkClick navItem={aboutItem} />
                  </Text>
                )}

                <Flex flexDir="column" gap="16px">
                  <FooterSectionTitle title="Insights" />
                  <Options options={getNavItem("insights")?.data || []} />
                </Flex>
              </SimpleGrid>
            </Show>
          </Flex>
          <Flex
            w="100%"
            justifyContent="space-between"
            py={{ base: 0, md: "32px" }}
            color="#FFF"
            fontWeight="400"
            fontSize="14px"
            lineHeight="20px"
            flexDir={{
              base: "column",
              md: "row",
            }}
            gap={{ base: "16px", md: "0" }}
          >
            <Text>© Copyright 2025 Kelp Dao. All rights reserved.</Text>
            <Flex alignItems="center" gap="4px">
              <UTMLink href="/terms-of-service" target="_blank">
                <Text>Terms of service</Text>
              </UTMLink>
              <Text>|</Text>
              <UTMLink href="/privacy-policy" target="_blank">
                <Text>Privacy policy</Text>
              </UTMLink>
            </Flex>
          </Flex>
        </Flex>
      </Box>
    </Center>
  );
};

export default Footer;
